import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import PageTemplate from "../../Templates/PageTemplate";
import TablePreset from "../../Table/TablePreset";
import { BackButton } from "../../HouseStyle/Components/Buttons";
import middleware from "../../Api/Middleware";
import ToastError from "../../Toasts/ToastError";
import { useTable } from "react-table";
import SortableHeader from "../../Table/TableComponents/SortableHeader";

function GiftcardOrders() {
    const navigate = useNavigate();
    const location = useLocation();
    const giftcard = location.state?.giftcard;
    const cardId = giftcard?.card_id;
    const [mutations, setMutations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page_size: 10,
        page: 1,
        max_items: 0,
        begin_of_page: 0,
        end_of_page: 0,
    });
    const [order, setOrder] = useState({ orderBy: '', direction: '' });
    const [searchBar, setSearchBar] = useState("");

    useEffect(() => {
        if (!cardId) {
            ToastError("Geen cadeaukaart geselecteerd");
            navigate("/algemene/instellingen/cadeaukaarten");
        }
    }, [cardId, navigate]);

    const fetchMutations = async () => {
        if (!cardId) {
            return;
        }
        setLoading(true);
        try {
            const searchQuery = searchBar.trim() ? `&searchTerm=${encodeURIComponent(searchBar.trim())}&noSearch=["giftcard_id", "balance_before", "balance_after", "title", "created_at", "updated_at"]` : "";
            const response = await middleware.get(
                `sp/giftcardMutationsTableAll?giftcard_id=${cardId}&page=${pagination.page}&page_size=${pagination.page_size}${order.orderBy !== '' && order.direction !== '' ? `&orderBy=${order.orderBy}&orderDirection=${order.direction}` : ''}${searchQuery}`
            );
            setMutations(response.data.data);
            setPagination(prev => ({
                ...prev,
                max_items: response.data.total,
                begin_of_page: response.data.from,
                end_of_page: response.data.to,
            }));
        } catch {
            ToastError("Fout bij het ophalen van de mutaties");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMutations();
    }, [pagination.page, pagination.page_size, order, searchBar, cardId]);

    const handleSearchChange = useCallback((value) => {
        setSearchBar(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const formatCardNumber = (num) => {
        if (!num) return "";
        const str = num.toString();
        if (str.length === 13) {
            return `${str[0]}-${str.slice(1, 7)}-${str.slice(7, 13)}`;
        }
        return str.match(/.{1,4}/g)?.join('-') || str;
    };

    const columns = useMemo(
        () => {
            let columnsArray = [
                {
                    Header: (
                        <SortableHeader currentItem={order} setItem={setOrder} field="mutation_id" pos={'start'}>
                            Mutatie
                        </SortableHeader>
                    ),
                    accessor: "mutation_id",
                    Cell: ({ row }) => <div className="text-start">{row.original.mutation_id}</div>,
                },
                {
                    Header: (
                        <SortableHeader currentItem={order} setItem={setOrder} field="balance_before" pos={'start'}>
                            Saldo voor
                        </SortableHeader>
                    ),
                    accessor: "balance_before",
                    Cell: ({ row }) => {
                        return <div className="text-start">€{row.original.balance_before}</div>
                    }
                },
                {
                    Header: (
                        <SortableHeader currentItem={order} setItem={setOrder} field="balance_after" pos={'start'}>
                            Saldo na
                        </SortableHeader>
                    ),
                    accessor: "balance_after",
                    Cell: ({ row }) => {
                        return <div className="text-start">€{row.original.balance_after}</div>
                    }
                },
                {
                    Header: (
                        <SortableHeader currentItem={order} setItem={setOrder} field="title" pos={'start'}>
                            Titel
                        </SortableHeader>
                    ),
                    accessor: "title",
                    Cell: ({ row }) => {
                        return <div className="text-start">{row.original.title}</div>
                    }
                },
                {
                    Header: (
                        <SortableHeader currentItem={order} setItem={setOrder} field="created_at" pos={'start'}>
                            Aangemaakt op
                        </SortableHeader>
                    ),
                    accessor: "created_at",
                    Cell: ({ row }) => {
                        return <div className="text-start">{row.original.created_at}</div>
                    }
                }
            ];
            return columnsArray;
        },
        [order.orderBy, order.direction, mutations]
    );

    const tableInstance = useTable({ columns, data: mutations });

    const cardNumberLabel = formatCardNumber(giftcard?.card_number);

    return (
        <PageTemplate navbarTitle={`Mutaties cadeaukaart #${cardNumberLabel || ''}`} pageIsLoading={loading}>
            <div className="w-100 d-flex flex-column">
                <div className="d-flex justify-content-between mb-4">
                    <BackButton text={"Terug"} disabled={false} onClickFunction={() => navigate("/algemene/instellingen/cadeaukaarten")} />
                </div>
                <div className="w-100 mt-2">
                    <TablePreset
                        data={[{ 'tableInstance': tableInstance, 'pagination': pagination, 'primary_key': 'mutation_id' }]}
                        tableLoading={loading}
                        setPagination={setPagination}
                        searchBar={[{ shown: true, searchFunction: handleSearchChange }]}
                    />
                </div>
            </div>
        </PageTemplate>
    );
}

export default GiftcardOrders;
